import React, { createContext, useContext, useMemo, useState } from 'react';
import { pills } from '../data/pills';
import { pregnancyPlans } from '../data/pregnancyPlans';
import { getPanel } from '../utils/biomarkers';
import { getRecommendedPillIds } from '../utils/pack';
import type { Biomarker, Goal, Order, Pill, PregnancyStage, User } from '../types/platform';
interface PlatformValue {
  authenticated: boolean;
  authenticate: () => void;
  switchProfile: () => void;
  user: User | null;
  goal: Goal | null;
  stage: PregnancyStage;
  selectedPlanId: PregnancyStage;
  customPillIds: string[];
  order: Order | null;
  panel: Biomarker[];
  packPills: Pill[];
  packPrice: number;
  signIn: (user: User) => void;
  signOut: () => void;
  chooseGoal: (goal: Goal, stage: PregnancyStage) => void;
  selectPlan: (id: PregnancyStage) => void;
  togglePill: (id: string) => void;
  placeOrder: () => void;
}
const PlatformContext = createContext<PlatformValue | null>(null);
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [stage, setStage] = useState<PregnancyStage>('trimester-2');
  const [selectedPlanId, setSelectedPlanId] = useState<PregnancyStage>('trimester-2');
  const [customPillIds, setCustomPillIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order | null>(null);
  const panel = useMemo(() => getPanel(goal ?? 'pregnancy'), [goal]);
  const value = useMemo<PlatformValue>(() => {
    const applyGoal = (g: Goal, s: PregnancyStage) => {
      setGoal(g);
      setStage(s);
      setSelectedPlanId(s);
      if (g === 'longevity') setCustomPillIds(getRecommendedPillIds(getPanel('longevity')));
      setOrder(null);
    };
    const ids = goal === 'pregnancy' ? pregnancyPlans.find(p => p.id === selectedPlanId)?.pillIds ?? [] : customPillIds;
    const packPills = ids.map(id => pills.find(p => p.id === id)).filter((p): p is Pill => Boolean(p));
    const packPrice = goal === 'pregnancy' ? pregnancyPlans.find(p => p.id === selectedPlanId)?.price ?? 0 : packPills.reduce((sum, p) => sum + p.price, 0);
    return {
      authenticated,
      authenticate: () => setAuthenticated(true),
      switchProfile: () => {
        setUser(null);
        setGoal(null);
        setOrder(null);
      },
      user,
      goal,
      stage,
      selectedPlanId,
      customPillIds,
      order,
      panel,
      packPills,
      packPrice,
      signIn: u => {
        setUser(u);
        applyGoal(u.goal, u.stage);
      },
      signOut: () => {
        setAuthenticated(false);
        setUser(null);
        setGoal(null);
        setOrder(null);
      },
      chooseGoal: (g, s) => {
        applyGoal(g, s);
        setUser(prev => prev ? {
          ...prev,
          goal: g,
          stage: s
        } : prev);
      },
      selectPlan: setSelectedPlanId,
      togglePill: id => setCustomPillIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]),
      placeOrder: () => setOrder({
        id: `NW-${Math.floor(100000 + Math.random() * 900000)}`,
        placedAt: new Date()
      })
    };
  }, [authenticated, user, goal, stage, selectedPlanId, customPillIds, order, panel]);
  return <PlatformContext.Provider value={value} data-id="element-135" data-mp="lx5z6s8re4dk">{children}</PlatformContext.Provider>;
}
export function usePlatform() {
  const ctx = useContext(PlatformContext);
  if (!ctx) throw new Error('usePlatform must be used within PlatformProvider');
  return ctx;
}